"use client";

import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui-system";
import { useDashboardBuilder } from "@/contexts/DashboardBuilderContext";
import { getDeviceById, listDevicesPage } from "@/lib/api/endpoints/devices";
import { listMetrics } from "@/lib/api/endpoints/metrics";
import type { JsonObject } from "@/lib/api/types/json";

import { mergeWidgetConfigJson } from "./dashboard-layout-utils";
import { parseStandardWidgetConfig, type AggregationKey, type TimeRangeKey } from "./widget-config";

const AGG: AggregationKey[] = ["avg", "min", "max"];
const TR: TimeRangeKey[] = ["last_1h", "last_24h", "last_7d"];

type WidgetConfigPanelProps = {
  tenantId: string;
};

export function WidgetConfigPanel({ tenantId }: WidgetConfigPanelProps) {
  const {
    editMode,
    selectedWidgetId,
    setSelectedWidgetId,
    baseWidgets,
    getDisplayWidget,
    replaceDirtyConfig,
  } = useDashboardBuilder();

  const [localError, setLocalError] = useState<string | null>(null);

  const base = useMemo(
    () => baseWidgets.find((w) => w.id === selectedWidgetId) ?? null,
    [baseWidgets, selectedWidgetId],
  );

  const display = useMemo(() => (base ? getDisplayWidget(base) : null), [base, getDisplayWidget]);

  const devicesQuery = useQuery({
    queryKey: ["builder-devices", tenantId],
    queryFn: () => listDevicesPage({ tenantId, limit: 200, offset: 0 }),
    enabled: editMode && Boolean(selectedWidgetId) && Boolean(tenantId),
    staleTime: 30_000,
  });

  const deviceId = typeof display?.config?.device_id === "string" ? display.config.device_id : "";

  const deviceQuery = useQuery({
    queryKey: ["device", deviceId],
    queryFn: () => getDeviceById(deviceId),
    enabled: editMode && Boolean(deviceId) && Boolean(selectedWidgetId),
  });

  const metricsQuery = useQuery({
    queryKey: ["tenant-metrics", tenantId],
    queryFn: () => listMetrics(tenantId),
    enabled: editMode && Boolean(tenantId) && Boolean(selectedWidgetId),
    staleTime: 60_000,
  });

  const channels = deviceQuery.data?.channels ?? [];

  useEffect(() => {
    setLocalError(null);
  }, [selectedWidgetId]);

  useEffect(() => {
    if (!editMode) {
      setSelectedWidgetId(null);
    }
  }, [editMode, setSelectedWidgetId]);

  if (!editMode || !selectedWidgetId || !base || !display) {
    return null;
  }

  const cfg = (display.config ?? {}) as JsonObject;

  const commitDraft = (draft: JsonObject) => {
    const channelKeys = channels.map((c) => c.key);
    const parsed = parseStandardWidgetConfig(draft, {
      channelKeys: channelKeys.length > 0 ? channelKeys : undefined,
    });
    if (!parsed.ok) {
      setLocalError(parsed.error);
      return;
    }
    setLocalError(null);
    replaceDirtyConfig(base.id, draft);
  };

  const patch = (updates: JsonObject) => {
    const draft = mergeWidgetConfigJson(cfg, updates);
    commitDraft(draft);
  };

  const title = typeof cfg.title === "string" ? cfg.title : "";
  const channel = typeof cfg.channel === "string" ? cfg.channel : "";
  const formula = typeof cfg.formula === "string" ? cfg.formula : "";
  const metricId = typeof cfg.metric_id === "string" ? cfg.metric_id : "";
  const derivedMode = Boolean(formula.trim() || metricId.trim());
  const aggregation = typeof cfg.aggregation === "string" ? cfg.aggregation : "avg";
  const time_range = typeof cfg.time_range === "string" ? cfg.time_range : "last_24h";
  const refresh =
    typeof cfg.refresh_interval_seconds === "number" && Number.isFinite(cfg.refresh_interval_seconds)
      ? cfg.refresh_interval_seconds
      : 30;
  const minV = typeof cfg.min_value === "number" ? String(cfg.min_value) : "";
  const maxV = typeof cfg.max_value === "number" ? String(cfg.max_value) : "";

  const items = devicesQuery.data?.items ?? [];

  return (
    <aside
      className="fixed bottom-0 right-0 top-0 z-[85] flex w-full max-w-md flex-col border-l border-card-border bg-card shadow-[0_24px_60px_-32px_rgba(15,23,42,0.24)]"
      aria-label="Widget configuration"
    >
      <div className="flex items-start justify-between gap-ds-3 border-b border-card-border bg-muted-bg/30 px-ds-4 py-ds-4">
        <div className="min-w-0">
          <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Selected widget</p>
          <p className="truncate text-body font-semibold text-foreground">
            {title || channel || formula || metricId.slice(0, 8) || base.type}
          </p>
          <p className="mt-1 text-[11px] leading-5 text-muted">Adjust source, timing, and display behavior for this tile.</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0 px-ds-2"
          aria-label="Close panel"
          onClick={() => setSelectedWidgetId(null)}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <div className="flex flex-1 flex-col gap-ds-4 overflow-y-auto px-ds-4 py-ds-4 text-caption">
        {localError ? (
          <p className="rounded-xl border border-error/30 bg-error/5 px-ds-3 py-ds-2 text-error" role="alert">
            {localError}
          </p>
        ) : null}
        <p className="rounded-2xl border border-card-border bg-muted-bg/30 px-ds-3 py-ds-3 text-[11px] leading-5 text-muted">
          Changes stay local until you press <span className="font-medium text-foreground">Save changes</span> in the
          canvas header.
        </p>
        <div className="grid gap-ds-4 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
          <div>
            <p className="form-section-title">Widget basics</p>
            <p className="mt-1 text-[11px] leading-5 text-muted">Set the visible title and choose which device this widget reads from.</p>
          </div>
          <label className="flex flex-col gap-ds-1.5">
            <span className="form-label">Title</span>
            <input
              className="ui-control py-ds-2"
              value={title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="Optional label"
            />
            <span className="form-helper">Leave blank to fall back to the bound channel, formula, or metric name.</span>
          </label>
          <label className="flex flex-col gap-ds-1.5">
            <span className="form-label form-label-required">Device</span>
            <select
              className="ui-control py-ds-2"
              value={deviceId}
              onChange={(e) => {
                const nextId = e.target.value;
                const draft = { ...cfg, device_id: nextId } as JsonObject;
                delete draft.formula;
                delete draft.metric_id;
                draft.channel = "";
                commitDraft(draft);
              }}
            >
              <option value="">Choose device…</option>
              {items.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name?.trim() || d.device_uid}
                </option>
              ))}
            </select>
            <span className="form-helper">Changing the device resets channel-bound sources so the config stays valid.</span>
          </label>
        </div>
        <div className="flex flex-col gap-ds-3 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
          <div>
            <p className="form-section-title">Data source</p>
            <p className="mt-1 text-[11px] leading-5 text-muted">Use a raw channel for direct telemetry or switch to a derived metric for computed values.</p>
          </div>
          <label className="flex cursor-pointer items-center gap-ds-2 text-caption">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={derivedMode}
              onChange={(e) => {
                if (e.target.checked) {
                  const draft = { ...cfg } as JsonObject;
                  delete draft.channel;
                  delete draft.metric_id;
                  draft.formula = formula.trim() || "temperature";
                  commitDraft(draft);
                } else {
                  const draft = { ...cfg } as JsonObject;
                  delete draft.formula;
                  delete draft.metric_id;
                  draft.channel = channel || channels[0]?.key || "";
                  commitDraft(draft);
                }
              }}
            />
            <span className="font-medium text-foreground">Use derived metric (formula or catalog)</span>
          </label>
          {derivedMode ? (
            <>
              <p className="text-[11px] leading-5 text-muted">
                Identifier names must match channel keys on the selected device. Tables still require a raw channel.
              </p>
              <label className="flex flex-col gap-ds-1.5">
                <span className="form-label">Source</span>
                <select
                  className="ui-control py-ds-2"
                  value={metricId ? "catalog" : "inline"}
                  onChange={(e) => {
                    const mode = e.target.value;
                    const draft = { ...cfg } as JsonObject;
                    delete draft.channel;
                    if (mode === "catalog") {
                      const first = metricsQuery.data?.[0]?.id;
                      if (first) {
                        delete draft.formula;
                        draft.metric_id = metricId || first;
                      } else {
                        delete draft.metric_id;
                        draft.formula = formula.trim() || "temperature";
                      }
                    } else {
                      delete draft.metric_id;
                      draft.formula = formula.trim() || "temperature";
                    }
                    commitDraft(draft);
                  }}
                >
                  <option value="inline">Custom formula</option>
                  <option value="catalog">Catalog metric</option>
                </select>
                <span className="form-helper">Catalog metrics stay standardized; formulas are better for one-off calculations.</span>
              </label>
              {metricId ? (
                <label className="flex flex-col gap-ds-1.5">
                  <span className="form-label">Metric</span>
                  <select
                    className="ui-control py-ds-2"
                    value={metricId}
                    onChange={(e) => {
                      const draft = { ...cfg } as JsonObject;
                      delete draft.channel;
                      delete draft.formula;
                      draft.metric_id = e.target.value;
                      commitDraft(draft);
                    }}
                    disabled={metricsQuery.isLoading}
                  >
                    <option value="">{metricsQuery.isLoading ? "Loading…" : "Choose metric…"}</option>
                    {(metricsQuery.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <span className="form-helper">Choose a saved metric definition already available in this workspace.</span>
                </label>
              ) : (
                <label className="flex flex-col gap-ds-1.5">
                  <span className="form-label">Formula</span>
                  <textarea
                    className="ui-control min-h-[6rem] resize-y py-ds-2  text-micro leading-relaxed"
                    value={formula}
                    placeholder="e.g. temperature * 1.8 + 32 or avg(temp_a, temp_b)"
                    spellCheck={false}
                    onChange={(e) => {
                      const draft = { ...cfg } as JsonObject;
                      delete draft.channel;
                      delete draft.metric_id;
                      draft.formula = e.target.value;
                      commitDraft(draft);
                    }}
                  />
                  <span className="form-helper">Reference device channel keys directly, for example <code>temperature * 1.8 + 32</code>.</span>
                </label>
              )}
            </>
          ) : (
            <label className="flex flex-col gap-ds-1.5">
              <span className="form-label form-label-required">Channel</span>
              <select
                className="ui-control py-ds-2"
                value={channel}
                onChange={(e) => patch({ channel: e.target.value })}
                disabled={!deviceId || deviceQuery.isLoading}
              >
                <option value="">{deviceId ? "Choose channel…" : "Select a device first"}</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.key}>
                    {c.label} ({c.key})
                  </option>
                ))}
              </select>
              <span className={deviceId ? "form-helper" : "form-helper-disabled"}>
                {deviceId ? "Pick the channel that should drive this widget." : "Select a device first to load available channels."}
              </span>
            </label>
          )}
        </div>
        <div className="grid gap-ds-4 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
          <div>
            <p className="form-section-title">Query behavior</p>
            <p className="mt-1 text-[11px] leading-5 text-muted">Tune how the widget rolls up telemetry and how often it refreshes.</p>
          </div>
          <label className="flex flex-col gap-ds-1.5">
            <span className="form-label">Aggregation</span>
            <select
              className="ui-control py-ds-2"
              value={aggregation}
              onChange={(e) => patch({ aggregation: e.target.value })}
            >
              {AGG.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-ds-1.5">
            <span className="form-label">Time range</span>
            <select
              className="ui-control py-ds-2"
              value={time_range}
              onChange={(e) => patch({ time_range: e.target.value })}
            >
              {TR.map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-ds-1.5">
            <span className="form-label">Refresh interval (seconds)</span>
            <input
              type="number"
              min={10}
              max={120}
              className="ui-control py-ds-2 tabular-nums"
              value={refresh}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n)) {
                  return;
                }
                patch({ refresh_interval_seconds: n });
              }}
            />
            <span className="form-helper">Use longer intervals for slower-changing signals to reduce dashboard churn.</span>
          </label>
        </div>
        {base.type === "gauge" ? (
          <div className="flex flex-col gap-ds-3 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
            <div>
              <p className="form-section-title">Gauge scale</p>
              <p className="mt-1 text-[11px] leading-5 text-muted">Override the natural series range when you need a fixed visual scale.</p>
            </div>
            <div className="grid grid-cols-2 gap-ds-2">
              <label className="flex flex-col gap-ds-1.5">
                <span className="form-label">Min</span>
                <input
                  className="ui-control py-ds-2 tabular-nums"
                  value={minV}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const draft = { ...cfg } as JsonObject;
                    if (raw === "") {
                      delete draft.min_value;
                    } else {
                      draft.min_value = Number(raw);
                    }
                    commitDraft(draft);
                  }}
                />
              </label>
              <label className="flex flex-col gap-ds-1.5">
                <span className="form-label">Max</span>
                <input
                  className="ui-control py-ds-2 tabular-nums"
                  value={maxV}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const draft = { ...cfg } as JsonObject;
                    if (raw === "") {
                      delete draft.max_value;
                    } else {
                      draft.max_value = Number(raw);
                    }
                    commitDraft(draft);
                  }}
                />
              </label>
            </div>
            <span className="form-helper">Leave either field empty to let the widget calculate its scale from live data.</span>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
